import React, { useState, useEffect, ReactNode } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowPathIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { cn } from "@/utils/utils";
import CreateEntityDrawer from "@/components/CreateEntityDrawer/CreateEntityDrawer";

export type BaseEntity = {
  id: string | number;
  name: string;
  color?: Color | null;
};

export interface Color {
  id: number;
  value: string;
}

export type FetchEntitiesFn<T extends BaseEntity> = (params: {
  searchTerm: string;
  page: number;
  pageSize: number;
}) => Promise<{
  data: T[];
  totalCount: number;
}>;

export type CreateEntityFn<T extends BaseEntity> = (params: {
  name: string;
}) => Promise<T>;

export interface EntityPickerModalProps<T extends BaseEntity> {
  isOpen: boolean;
  onClose: () => void;
  fetchEntities: FetchEntitiesFn<T>;
  createEntity?: CreateEntityFn<T>;
  initialSelectedEntity: T | null;
  onConfirm: (selectedEntity: T | null) => void;
  entityNameSingular?: string;
  entityNamePlural?: string;
  renderEntityRow?: (
    entity: T,
    isSelected: boolean,
    toggle: (ent: T) => void,
  ) => ReactNode;
  /** Allow confirming with nothing selected (clears the value). */
  allowEmpty?: boolean;
}

export default function SingleEntityPickerModal<T extends BaseEntity>({
  isOpen,
  onClose,
  fetchEntities,
  createEntity,
  initialSelectedEntity,
  onConfirm,
  entityNameSingular = "Entity",
  entityNamePlural = "Entities",
  renderEntityRow,
  allowEmpty = false,
}: EntityPickerModalProps<T>) {
  const [searchTerm, setSearchTerm] = useState("");
  const [entities, setEntities] = useState<T[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<T | null>(
    initialSelectedEntity,
  );
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showCreateDrawer, setShowCreateDrawer] = useState(false);
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 500);

  useEffect(() => {
    if (isOpen) {
      setSelectedEntity(initialSelectedEntity);
      setSearchTerm("");
      setEntities([]);
      setPage(1);
      setHasMore(true);
      setIsLoadingInitial(false);
      setShowCreateDrawer(false);
    }
  }, [isOpen, initialSelectedEntity]);

  useEffect(() => {
    if (!isOpen) return;
    setEntities([]);
    setPage(1);
    setHasMore(true);
    loadEntities(1, debouncedSearchTerm, true);
  }, [isOpen, debouncedSearchTerm]);

  const loadEntities = (
    pageNumber: number,
    sTerm: string,
    isInitial: boolean = false,
  ) => {
    if (isInitial) {
      setIsLoadingInitial(true);
    } else {
      setIsLoadingMore(true);
    }

    fetchEntities({
      searchTerm: sTerm,
      page: pageNumber,
      pageSize: 10,
    })
      .then((res) => {
        const newData = res.data;
        setEntities((prev) =>
          pageNumber === 1 ? newData : [...prev, ...newData],
        );
        if (newData.length < 10) {
          setHasMore(false);
        }
      })
      .catch(() => {
        setHasMore(false);
      })
      .finally(() => {
        setIsLoadingInitial(false);
        setIsLoadingMore(false);
      });
  };

  const fetchNext = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadEntities(nextPage, debouncedSearchTerm, false);
  };

  const toggleSelection = (ent: T) => {
    setSelectedEntity((prev) => {
      if (prev?.id === ent.id) {
        return null;
      }
      return ent;
    });
  };

  const handleEntityCreated = (newEntity: T) => {
    setEntities((prev) => [newEntity, ...prev]);
    setSelectedEntity(newEntity);
  };

  const defaultRenderRow = (ent: T, isSelected: boolean) => (
    <button
      className={cn(
        "w-full px-3 py-2 text-left transition-colors",
        isSelected
          ? "bg-gray-100 dark:bg-gray-800"
          : "hover:bg-gray-50 dark:hover:bg-gray-800/50",
      )}
      key={ent.id}
      onClick={() => toggleSelection(ent)}
    >
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-medium text-gray-900 dark:text-white truncate">
            {ent.name}
          </div>
        </div>
        {isSelected && (
          <CheckIcon className="h-3.5 w-3.5 text-primary flex-shrink-0 ml-2" />
        )}
      </div>
    </button>
  );

  const renderRow = (ent: T) => {
    const isSelected = selectedEntity?.id === ent.id;
    if (renderEntityRow) {
      return (
        <React.Fragment key={ent.id}>
          {renderEntityRow(ent, isSelected, toggleSelection)}
        </React.Fragment>
      );
    }
    return defaultRenderRow(ent, isSelected);
  };

  const renderContent = () => {
    if (isLoadingInitial) {
      return (
        <div className="flex h-[280px] items-center justify-center">
          <div className="flex flex-col items-center">
            <ArrowPathIcon className="h-4 w-4 animate-spin text-gray-400" />
            <span className="mt-2 text-[13px] text-gray-500 dark:text-gray-400">
              Loading...
            </span>
          </div>
        </div>
      );
    }

    return (
      <InfiniteScroll
        dataLength={entities.length}
        endMessage={
          entities.length > 0 ? (
            <div className="py-2 text-center text-[12px] text-gray-400">
              End of list
            </div>
          ) : (
            <div className="py-6 text-center">
              <p className="text-[13px] text-gray-500 dark:text-gray-400">
                No {entityNamePlural.toLowerCase()} found
              </p>
            </div>
          )
        }
        hasMore={hasMore && !isLoadingMore}
        height={280}
        loader={
          isLoadingMore ? (
            <div className="flex items-center justify-center py-2">
              <ArrowPathIcon className="h-3.5 w-3.5 animate-spin text-gray-400" />
            </div>
          ) : null
        }
        next={fetchNext}
      >
        <div>{entities.map(renderRow)}</div>
      </InfiniteScroll>
    );
  };

  return (
    <>
      <Dialog className="relative z-50" onClose={onClose} open={isOpen}>
        <DialogBackdrop className="fixed inset-0 bg-black/20 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150" />

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <DialogPanel className="relative w-full max-w-sm transform overflow-hidden bg-white dark:bg-gray-900 shadow-lg transition-all data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 border border-gray-200 dark:border-gray-700">
              {/* Header */}
              <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-200 dark:border-gray-700">
                <DialogTitle className="text-sm font-medium text-gray-900 dark:text-white">
                  Select {entityNameSingular}
                </DialogTitle>
                <div className="flex items-center gap-2">
                  {createEntity && (
                    <button
                      className="text-[12px] font-medium text-primary hover:text-primary-dark transition-colors"
                      onClick={() => setShowCreateDrawer(true)}
                    >
                      + New
                    </button>
                  )}
                  <button
                    aria-label="Close"
                    className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    onClick={onClose}
                  >
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              {/* Search */}
              <div className="px-3 py-2.5 border-b border-gray-200 dark:border-gray-700">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <input
                    className="w-full border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 pl-7 pr-3 py-1.5 text-[13px] text-gray-900 dark:text-white placeholder-muted-foreground focus:border-gray-300 dark:focus:border-gray-600 focus:outline-none transition-colors"
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={`Search ${entityNamePlural.toLowerCase()}...`}
                    type="text"
                    value={searchTerm}
                  />
                </div>
              </div>

              {/* Entity List */}
              <div>{renderContent()}</div>

              {/* Footer */}
              <div className="flex gap-2 px-3 py-2.5 border-t border-gray-200 dark:border-gray-700">
                <button
                  className="flex-1 px-2.5 py-1.5 text-[13px] font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  onClick={onClose}
                  type="button"
                >
                  Cancel
                </button>
                <button
                  className={cn(
                    "flex-1 px-2.5 py-1.5 text-[13px] font-medium text-white transition-colors",
                    selectedEntity || allowEmpty
                      ? "bg-primary hover:bg-primary/90"
                      : "bg-muted-foreground/50 cursor-not-allowed",
                  )}
                  disabled={!selectedEntity && !allowEmpty}
                  onClick={() => onConfirm(selectedEntity)}
                  type="button"
                >
                  {selectedEntity || !allowEmpty ? "Confirm" : "Confirm (none)"}
                </button>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>

      {createEntity && (
        <CreateEntityDrawer
          createEntity={createEntity}
          entityNameSingular={entityNameSingular}
          isOpen={showCreateDrawer}
          onClose={() => setShowCreateDrawer(false)}
          onEntityCreated={handleEntityCreated}
        />
      )}
    </>
  );
}
